/**
 * BetUdontBet — SVG Icon Library
 * All poker game symbols as pure SVG React components.
 * Zero emoji — every symbol is a scalable, colourable SVG.
 */

import React from 'react';

type IconProps = {
  size?: number | string;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
};

// ── Card Suit Icons ─────────────────────────────────────────────────────────

export function SpadeIcon({ size = 20, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 24 24"
      fill={color}
      className={className}
      style={style}
      aria-label="Spades"
    >
      {/* Spade: inverted heart top + stem */}
      <path d="M12 2C10.5 4.5 7 6.5 5 9C4 10.5 4 12.5 5.5 13.5C6.5 14.2 7.8 14.2 9 13.5C8.5 15.2 7.5 16.5 6 17H11V21H13V17H18C16.5 16.5 15.5 15.2 15 13.5C16.2 14.2 17.5 14.2 18.5 13.5C20 12.5 20 10.5 19 9C17 6.5 13.5 4.5 12 2Z" />
    </svg>
  );
}

export function HeartIcon({ size = 20, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 24 24"
      fill={color}
      className={className}
      style={style}
      aria-label="Hearts"
    >
      <path d="M12 21.5C12 21.5 2 14 2 7.5C2 5 4 3 6.5 3C8.5 3 10.2 4.2 12 6C13.8 4.2 15.5 3 17.5 3C20 3 22 5 22 7.5C22 14 12 21.5 12 21.5Z" />
    </svg>
  );
}

export function DiamondIcon({ size = 20, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 24 24"
      fill={color}
      className={className}
      style={style}
      aria-label="Diamonds"
    >
      <path d="M12 2L22 12L12 22L2 12Z" />
    </svg>
  );
}

export function ClubIcon({ size = 20, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 24 24"
      fill={color}
      className={className}
      style={style}
      aria-label="Clubs"
    >
      {/* Three circles + stem */}
      <circle cx="12" cy="7" r="3.5" />
      <circle cx="7.5" cy="13" r="3.5" />
      <circle cx="16.5" cy="13" r="3.5" />
      <path d="M10.5 17C10.5 17 10 18.5 9 19.5H11V21H13V19.5H15C14 18.5 13.5 17 13.5 17H10.5Z" />
    </svg>
  );
}

/** Renders the correct suit icon for a given suit name */
export function SuitIcon({ suit, size = 20, color, className = '', style }: IconProps & { suit: string }) {
  const suits: Record<string, string> = {
    hearts: '#e53e3e', heart: '#e53e3e', h: '#e53e3e',
    diamonds: '#e53e3e', diamond: '#e53e3e', d: '#e53e3e',
    clubs: '#1a202c', club: '#1a202c', c: '#1a202c',
    spades: '#1a202c', spade: '#1a202c', s: '#1a202c',
  };
  const resolvedColor = color ?? suits[suit.toLowerCase()] ?? '#1a202c';

  const props = { size, color: resolvedColor, className, style };
  const s = suit.toLowerCase();
  if (s === 'hearts' || s === 'heart' || s === 'h') return <HeartIcon {...props} />;
  if (s === 'diamonds' || s === 'diamond' || s === 'd') return <DiamondIcon {...props} />;
  if (s === 'clubs' || s === 'club' || s === 'c') return <ClubIcon {...props} />;
  return <SpadeIcon {...props} />;
}

// ── Suit Color Helpers ──────────────────────────────────────────────────────

export function getSuitColor(suit: string): string {
  const s = suit.toLowerCase();
  return s === 'hearts' || s === 'heart' || s === 'diamonds' || s === 'diamond'
    ? '#dc2626'
    : '#111827';
}

// ── Poker Chip SVGs ─────────────────────────────────────────────────────────

const CHIP_DENOM: Record<number, { bg: string; ring: string; label: string }> = {
  1:     { bg: '#9ca3af', ring: '#6b7280',  label: '1'    },
  5:     { bg: '#ef4444', ring: '#b91c1c',  label: '5'    },
  10:    { bg: '#3b82f6', ring: '#1d4ed8',  label: '10'   },
  25:    { bg: '#22c55e', ring: '#15803d',  label: '25'   },
  50:    { bg: '#a855f7', ring: '#7e22ce',  label: '50'   },
  100:   { bg: '#000000', ring: '#374151',  label: '100'  },
  500:   { bg: '#eab308', ring: '#a16207',  label: '500'  },
  1000:  { bg: '#ec4899', ring: '#be185d',  label: '1K'   },
  5000:  { bg: '#f97316', ring: '#c2410c',  label: '5K'   },
  10000: { bg: '#8b5cf6', ring: '#6d28d9',  label: '10K'  },
};

export function PokerChipIcon({
  denomination = 1,
  size = 32,
  className = '',
  style,
}: {
  denomination?: number;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const closest = Object.keys(CHIP_DENOM)
    .map(Number)
    .sort((a, b) => Math.abs(a - denomination) - Math.abs(b - denomination))[0];
  const { bg, ring, label } = CHIP_DENOM[closest];
  const sz: number = typeof size === 'number' ? size : 32;
  const r = sz / 2;

  return (
    <svg
      width={sz} height={sz}
      viewBox={`0 0 ${sz} ${sz}`}
      className={className}
      style={style}
      aria-label={`${denomination} chip`}
    >
      {/* Main disc */}
      <circle cx={r} cy={r} r={r - 1} fill={bg} stroke={ring} strokeWidth={2} />
      {/* Inner ring */}
      <circle cx={r} cy={r} r={r * 0.7} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={2} strokeDasharray="4 3" />
      {/* Center fill */}
      <circle cx={r} cy={r} r={r * 0.48} fill="rgba(0,0,0,0.35)" />
      {/* Label */}
      <text
        x={r} y={r + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontSize={sz * 0.28}
        fontWeight="800"
        fontFamily="'Bebas Neue', 'DM Sans', sans-serif"
        letterSpacing="0.5"
      >
        {label}
      </text>
    </svg>
  );
}

// ── Dealer Button ───────────────────────────────────────────────────────────

export function DealerButtonIcon({ size = 28, className = '', style }: IconProps) {
  const sz: number = typeof size === 'number' ? size : 28;
  const r = sz / 2;
  return (
    <svg
      width={sz} height={sz}
      viewBox={`0 0 ${sz} ${sz}`}
      className={className}
      style={style}
      aria-label="Dealer button"
    >
      <circle cx={r} cy={r} r={r - 1} fill="#f8fafc" stroke="#cbd5e1" strokeWidth={1.5} />
      <circle cx={r} cy={r} r={r * 0.75} fill="none" stroke="#94a3b8" strokeWidth={1} />
      <text
        x={r} y={r + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#0f172a"
        fontSize={sz * 0.38}
        fontWeight="900"
        fontFamily="'Bebas Neue', 'DM Sans', sans-serif"
      >
        D
      </text>
    </svg>
  );
}

// ── Game Type Icons (for lobby table cards & feature sections) ──────────────

export function CardTableIcon({ size = 24, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      className={className} style={style}>
      {/* Oval table */}
      <ellipse cx="12" cy="12" rx="10" ry="6" />
      {/* Two cards */}
      <rect x="8" y="8" width="3.5" height="5" rx="0.5" fill={color} stroke="none" opacity="0.8" />
      <rect x="12.5" y="8" width="3.5" height="5" rx="0.5" fill={color} stroke="none" opacity="0.6" />
    </svg>
  );
}

export function SlotMachineIcon({ size = 24, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
      className={className} style={style}>
      {/* Cabinet */}
      <rect x="4" y="3" width="16" height="18" rx="2" />
      {/* Screen / reels area */}
      <rect x="7" y="6" width="10" height="8" rx="1" fill={color} opacity="0.15" />
      {/* Reel dividers */}
      <line x1="11" y1="6" x2="11" y2="14" />
      <line x1="15" y1="6" x2="15" y2="14" strokeOpacity="0.5" />
      {/* Lever */}
      <line x1="20" y1="8" x2="22" y2="8" strokeWidth={2} />
      <circle cx="22" cy="7" r="1" fill={color} stroke="none" />
      {/* Button row */}
      <circle cx="9" cy="18" r="1.5" fill={color} stroke="none" opacity="0.6" />
      <circle cx="12" cy="18" r="1.5" fill={color} stroke="none" opacity="0.8" />
      <circle cx="15" cy="18" r="1.5" fill={color} stroke="none" opacity="0.6" />
    </svg>
  );
}

/** Official GBUX coin art — replaces flat SVG placeholder */
export function GbuxTokenIcon({ size = 24, color = '#00eeff', className = '', style }: IconProps) {
  return (
    <img
      src="/media/gbux-logo.png"
      alt="GBUX"
      width={size}
      height={size}
      className={className}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        display: "inline-block",
        verticalAlign: "middle",
        filter: color && color !== "#00eeff" ? undefined : undefined,
        ...style,
      }}
      draggable={false}
    />
  );
}

export function TrophyBadgeIcon({ size = 24, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}
      className={className} style={style}>
      <path d="M7 2V8C7 11.3 9.2 14.1 12 14.9C14.8 14.1 17 11.3 17 8V2H7Z" opacity="0.9" />
      <path d="M5 2H7V5H5C4.4 5 4 4.6 4 4V3C4 2.4 4.4 2 5 2Z" />
      <path d="M17 2H19C19.6 2 20 2.4 20 3V4C20 4.6 19.6 5 19 5H17V2Z" />
      <path d="M11 15V18H9L8 20H16L15 18H13V15C12.7 15 12.3 15 12 15C11.7 15 11.3 15 11 15Z" />
      <rect x="8" y="20" width="8" height="2" rx="1" />
    </svg>
  );
}

export function ShieldLockIcon({ size = 24, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}
      className={className} style={style}>
      <path d="M12 2L4 5V11C4 15.4 7.4 19.5 12 21C16.6 19.5 20 15.4 20 11V5L12 2Z" opacity="0.85" />
      <circle cx="12" cy="11" r="2" fill="rgba(0,0,0,0.5)" />
      <rect x="10" y="11" width="4" height="4" rx="0.5" fill="rgba(0,0,0,0.5)" />
    </svg>
  );
}

export function ZapBoltIcon({ size = 24, color = 'currentColor', className = '', style }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}
      className={className} style={style}>
      <path d="M13 2L4 14H11V22L20 10H13V2Z" />
    </svg>
  );
}

// ── Suit text label (used in corner of playing cards) ──────────────────────

export function SuitText({ suit, size = 14, className = '' }: { suit: string; size?: number; className?: string }) {
  const color = getSuitColor(suit);
  const s = suit.toLowerCase();
  return (
    <SuitIcon
      suit={s}
      size={size}
      color={color}
      className={className}
    />
  );
}

// ── Action button icons (for poker controls) ────────────────────────────────

export function FoldIcon({ size = 18, color = 'currentColor', className = '' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
      className={className}>
      {/* Two cards being tossed away */}
      <rect x="3" y="6" width="8" height="12" rx="1" transform="rotate(-15 7 12)" />
      <rect x="13" y="6" width="8" height="12" rx="1" transform="rotate(10 17 12)" />
      <line x1="18" y1="4" x2="22" y2="3" />
      <line x1="6" y1="4" x2="2" y2="3" />
    </svg>
  );
}

export function CheckHandIcon({ size = 18, color = 'currentColor', className = '' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
      className={className}>
      <polyline points="4 14 10 20 20 8" />
    </svg>
  );
}

export function CallIcon({ size = 18, color = 'currentColor', className = '' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      className={className}>
      {/* Coin moving right */}
      <circle cx="8" cy="12" r="5" />
      <path d="M13 12H22M18 8L22 12L18 16" />
    </svg>
  );
}

export function RaiseIcon({ size = 18, color = 'currentColor', className = '' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      className={className}>
      {/* Arrow up + chip stack */}
      <circle cx="12" cy="17" r="4" />
      <path d="M12 13V5M8 9L12 5L16 9" />
    </svg>
  );
}

export function AllInIcon({ size = 18, color = 'currentColor', className = '' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      className={className}>
      {/* Stack of chips + double arrow */}
      <circle cx="12" cy="16" r="3" />
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="8"  r="3" />
      <path d="M18 4L22 8M22 8L18 12M22 8H15" />
    </svg>
  );
}

// ── Robot / AI player avatar ─────────────────────────────────────────────────

export function AiPlayerIcon({ size = 24, color = 'currentColor', className = '' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
      className={className}>
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M8 8V6M16 8V6" />
      <circle cx="9" cy="14" r="2" fill={color} stroke="none" />
      <circle cx="15" cy="14" r="2" fill={color} stroke="none" />
      <path d="M9 18H15" />
      <path d="M12 4V6" />
      <circle cx="12" cy="4" r="1" fill={color} stroke="none" />
    </svg>
  );
}

// ── BUDB logo mark SVG ───────────────────────────────────────────────────────

export function BudbLogoMark({ size = 40, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" className={className}>
      {/* Chip base */}
      <circle cx="20" cy="20" r="19" fill="url(#budbGrad)" />
      <circle cx="20" cy="20" r="15" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeDasharray="5 4" />
      <circle cx="20" cy="20" r="11" fill="rgba(0,0,0,0.3)" />
      {/* Suit marks at quarters */}
      <text x="20" y="23" textAnchor="middle" dominantBaseline="middle"
        fill="white" fontSize="10" fontWeight="900" fontFamily="'Bebas Neue', sans-serif">
        BUDB
      </text>
      <defs>
        <linearGradient id="budbGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7744ff" />
          <stop offset="100%" stopColor="#4422cc" />
        </linearGradient>
      </defs>
    </svg>
  );
}
